import React, { useState, useEffect } from 'react';
import { Flame, Eye, Clock, TrendingUp, Users, Zap, Percent } from 'lucide-react';

// Generate pseudo-random but consistent numbers based on tour ID
const getConsistentRandom = (tourId, seed, min, max) => {
  const hash = (tourId + seed).split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  return min + Math.abs(hash) % (max - min + 1);
};

// Popularity Badge for Tour Cards (uses tour-specific settings)
export function PopularityBadge({ tour, className = '' }) {
  const bookingsLast24h = getConsistentRandom(tour.id, 'bookings', 3, 12);
  const isVeryPopular = tour.rating >= 4.7 || tour.reviews_count > 150;
  const isSellingFast = bookingsLast24h >= 8;

  // Check tour-specific settings (default to true if not set)
  const showPopular = tour.show_popular_badge !== false;
  const showSellingFast = tour.show_selling_fast_badge !== false;

  if (isVeryPopular && showPopular) {
    return (
      <div className={`flex items-center gap-1 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold ${className}`}>
        <Flame className="h-3 w-3" />
        <span>Very Popular</span>
      </div>
    );
  }

  if (isSellingFast && showSellingFast) {
    return (
      <div className={`flex items-center gap-1 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold ${className}`}>
        <TrendingUp className="h-3 w-3" />
        <span>Selling Fast</span>
      </div>
    );
  }

  return null;
}

// Discount Badge
export function DiscountBadge({ tour, className = '' }) {
  if (!tour.discount_percentage || tour.discount_percentage <= 0) return null;

  return (
    <div className={`flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold ${className}`}>
      <Percent className="h-3 w-3" />
      <span>{tour.discount_percentage}% OFF</span>
    </div>
  );
}

// Discount Label Badge (e.g., "Early Bird", "Holiday Special")
export function DiscountLabelBadge({ tour, className = '' }) {
  if (!tour.discount_label) return null;

  return (
    <div className={`flex items-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold ${className}`}>
      <span>{tour.discount_label}</span>
    </div>
  );
}

// Spots Left Badge (uses tour-specific settings)
export function SpotsLeftBadge({ tour, className = '' }) {
  const spotsLeft = getConsistentRandom(tour.id, 'spots', 2, 6);
  const showSpotsLeft = tour.show_spots_left !== false;
  
  if (spotsLeft <= 4 && showSpotsLeft) {
    return (
      <div className={`flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium ${className}`}>
        <Users className="h-3 w-3" />
        <span>Only {spotsLeft} spots left</span>
      </div>
    );
  }
  return null;
}

// Live Viewers Counter (uses tour-specific settings)
export function LiveViewers({ tour }) {
  const [viewers, setViewers] = useState(0);
  const showLiveViewers = tour.show_live_viewers !== false;

  useEffect(() => {
    if (!showLiveViewers) return;
    
    // Initial count based on tour
    const baseViewers = getConsistentRandom(tour.id, 'viewers', 5, 18);
    setViewers(baseViewers);

    // Simulate fluctuation
    const interval = setInterval(() => {
      setViewers(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const newValue = prev + change;
        return Math.max(3, Math.min(25, newValue));
      });
    }, 8000);

    return () => clearInterval(interval);
  }, [tour.id, showLiveViewers]);

  if (!showLiveViewers) return null;

  return (
    <div className="flex items-center gap-2 text-sm">
      <div className="flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
        <Eye className="h-4 w-4 text-muted-foreground" />
      </div>
      <span className="text-muted-foreground">
        <span className="font-semibold text-foreground">{viewers} people</span> viewing now
      </span>
    </div>
  );
}

// Recent Bookings Counter (uses tour-specific settings)
export function RecentBookings({ tour }) {
  const bookings = getConsistentRandom(tour.id, 'recent', 4, 15);
  const showRecentBookings = tour.show_recent_bookings !== false;
  
  if (!showRecentBookings) return null;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <Zap className="h-4 w-4 text-orange-500" />
      <span className="text-muted-foreground">
        <span className="font-semibold text-foreground">{bookings} bookings</span> in the last 24 hours
      </span>
    </div>
  );
}

// Book Ahead Warning (uses tour-specific settings)
export function BookAheadBadge({ tour }) {
  const isHighDemand = tour.rating >= 4.5 && tour.reviews_count > 100;
  const showBookAhead = tour.show_book_ahead_badge !== false;
  
  if (!isHighDemand || !showBookAhead) return null;

  return (
    <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium">
      <Clock className="h-4 w-4" />
      <span>Book ahead – Very Popular!</span>
    </div>
  );
}

// Recently Booked Toast Notification (uses tour-specific settings)
export function RecentBookingToast({ tour, onClose }) {
  const [visible, setVisible] = useState(false);
  const showBookingToast = tour.show_booking_toast !== false;
  
  const names = ['Sarah', 'Mike', 'Emma', 'John', 'Lisa', 'David', 'Maria', 'Carlos', 'Ana', 'James'];
  const cities = ['New York', 'Miami', 'Los Angeles', 'Chicago', 'Houston', 'Boston', 'Seattle', 'Denver'];
  
  const nameIndex = getConsistentRandom(tour.id, 'name', 0, names.length - 1);
  const cityIndex = getConsistentRandom(tour.id, 'city', 0, cities.length - 1);
  const hoursAgo = getConsistentRandom(tour.id, 'hours', 1, 5);

  useEffect(() => {
    if (!showBookingToast) return;
    
    // Show after 3 seconds
    const showTimer = setTimeout(() => setVisible(true), 3000);
    
    // Hide after 8 seconds
    const hideTimer = setTimeout(() => {
      setVisible(false);
      if (onClose) onClose();
    }, 11000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [tour.id, onClose, showBookingToast]);

  if (!visible || !showBookingToast) return null;

  return (
    <div className="fixed bottom-24 left-4 z-50 animate-in slide-in-from-left duration-300">
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-xs">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {names[nameIndex]} from {cities[cityIndex]}
            </p>
            <p className="text-xs text-gray-500">
              booked this tour {hoursAgo} hour{hoursAgo > 1 ? 's' : ''} ago
            </p>
          </div>
          <button 
            onClick={() => { setVisible(false); if (onClose) onClose(); }}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

// Urgency Strip for Detail Page Header (uses tour-specific settings)
export function UrgencyStrip({ tour }) {
  const spotsLeft = getConsistentRandom(tour.id, 'spots', 2, 6);
  const bookingsToday = getConsistentRandom(tour.id, 'today', 3, 9);
  
  const showPopular = tour.show_popular_badge !== false;
  const showSpotsLeft = tour.show_spots_left !== false;
  const showRecentBookings = tour.show_recent_bookings !== false;
  
  return (
    <div className="flex flex-wrap gap-3 items-center">
      {/* Discount Badge First */}
      {tour.discount_percentage > 0 && (
        <div className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold">
          <Percent className="h-3.5 w-3.5" />
          {tour.discount_percentage}% OFF
        </div>
      )}
      {tour.discount_label && (
        <div className="flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
          {tour.discount_label}
        </div>
      )}
      {tour.rating >= 4.7 && showPopular && (
        <div className="flex items-center gap-1.5 bg-orange-100 text-orange-800 px-3 py-1.5 rounded-full text-xs font-semibold">
          <Flame className="h-3.5 w-3.5" />
          Very Popular
        </div>
      )}
      {spotsLeft <= 4 && showSpotsLeft && (
        <div className="flex items-center gap-1.5 bg-red-100 text-red-800 px-3 py-1.5 rounded-full text-xs font-semibold">
          <Users className="h-3.5 w-3.5" />
          Only {spotsLeft} spots left tomorrow
        </div>
      )}
      {bookingsToday >= 5 && showRecentBookings && (
        <div className="flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-xs font-semibold">
          <TrendingUp className="h-3.5 w-3.5" />
          {bookingsToday} booked today
        </div>
      )}
    </div>
  );
}

// Limited Availability Badge for CTA (uses tour-specific settings)
export function LimitedAvailabilityBadge({ tour }) {
  const showLimitedAvailability = tour.show_limited_availability !== false;
  
  if (!showLimitedAvailability) return null;
  
  return (
    <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-semibold mb-3">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
      </span>
      Limited Availability
    </div>
  );
}
